import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNewEntityTag , useRealtimeDrivers} from 'Core/hooks';
import moment from 'moment';
import _ from 'lodash';

//Components
import {
    SidePanel,
    SidePanelBody,
    SidePanelFooter,
    Button,
    Select,
    TimeFrame,
    useTimeFrameComponent,
    Form
} from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import { catalogLoad } from 'Redux/actions'
import {
    openModalTrackerAdvancedFilters,
    workOrdersSearchWorkOrderStatuses,
    workOrdersSettingsSearchWorkOrderTypes,
    workOrdersCustomerSchedule,
    reset
} from './redux/slice';
import TagsManage from 'Modules/.common/components/TagsManage';

//Helpers
import { getInitialRange } from "Core/data/Helpers";

// Styles
import './index.scss';
const AdvancedFilters = ({ tableRef, formData, setFormData, initialState, applyChanges, hasFilters, savePreferences, paginationTracker, viewSelected, buildConditions }) => {

    const dispatch = useDispatch()
    const { messages } = useIntl()

    // Redux States
    const { open, loading, statuses, workOrderTypes, customers, loadingCustomers } = useSelector((state) => state.workOrdersTrackerTabAdvancedFiltersRedux);
    const loadingDrivers = useSelector(state => state.catalogsRedux.loadingDrivers);
    const driversFromState = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone } = user;

    // Sort drivers alphabetically by name using lodash (case insensitive)
    const drivers = useMemo(() => {
        return _.orderBy(driversFromState || [], [driver => driver.name.toLowerCase()], ['asc']);
    }, [driversFromState]);

    let severityList = [
        { id: "Low", name: messages['workOrder_createEdit_low'] },
        { id: "Medium", name: messages['workOrder_createEdit_medium'] },
        { id: "High", name: messages['workOrder_createEdit_high'] }
    ]

    const timeFrameOptions = [
        { id: "custom", label: messages['date_custom'] },
        { id: "today", label: messages['date_today'] },
        { id: "yesterday", label: messages['date_yesterday'] },
        { id: "week", label: messages['date_this_week'] },
        { id: "lastWeek", label: messages['date_last_week'] },
        { id: "thisMonth", label: messages['date_this_month'] },
        { id: "nextMonth", label: messages['date_this_nextMonth'] },
        { id: "lastMonth", label: messages['date_last_month'] },
        { id: "quarter", label: messages['date_this_quarter'] },
        { id: "lastQuarter", label: messages['date_last_quarter'] },
        { id: "next7days", label: messages['date_next_7days'] },
        { id: "next15days", label: messages['date_next_15days'] },
        { id: "next30Days", label: messages['date_next_30days'] },
        { id: "next90Days", label: messages['date_next_90days'] },

    ];

    const entityTags = useNewEntityTag(
        ["Driver"],
        messages['reportsMainRT_viewReportBy'],
        "calc(100vh - 320px)",
        messages['reportsMainRT_runReportFor'],
        'hideTags'
    )

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "thisMonth")

    //Form refs
    const formRef = Form.useForm();

    useEffect(() => {
        if (open) {
            dispatch(workOrdersSearchWorkOrderStatuses({}))
            dispatch(workOrdersSettingsSearchWorkOrderTypes({}))
            dispatch(workOrdersCustomerSchedule({}))
            dispatch(catalogLoad(["drivers"]));
        }
    }, [open])

    useEffect(() => {
        if (open) {

            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);
            let start = moment(date?.start).utc();
            let end = moment(date?.end).utc();



            let currentDateRange = {
                start: start.toISOString(),
                end: end.toISOString()
            }

            setFormData({ ...formData, rangeDate: currentDateRange, timeFrame: timeFrame?.option?.id })
            savePreferences([
                {
                    key: "rangeDate",
                    value: currentDateRange
                },
                {
                    key: "timeFrame",
                    value: timeFrame?.option?.id
                }
            ])
        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    useEffect(() => {
        return () => {
            dispatch(reset())
        }
    }, [])

    useEffect(() => {
        if (open && formData) {
            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
                timeFrame.onChangeSelect(currentTime, formData.rangeDate.start, formData.rangeDate.end)
            }
            formRef?.setValues(formData);
        }
    }, [open])

    const clearFilters = () => {
        const clearFormData = {
            ...initialState,
            forceRefreshMemcached: true
        };

        setFormData(clearFormData);

        paginationTracker.refresh({
            _page: 1,
            _pageSize: 10,
            _conditions: buildConditions(clearFormData, viewSelected, timeZone)
        });

        tableRef.addConditions(1, [{
            range: {
                fields: ["scheduledDate"],
                start: initialState?.rangeDate?.start,
                end: initialState?.rangeDate?.end
            }
        }])

        const defaultPreferences = ["severities", "types", "status", "rangeDate", "timeFrame", 'drivers', 'customers', 'tags']
            .map(key => ({ key, value: null }));

        savePreferences(defaultPreferences)
        setFormData(initialState);
        formRef?.resetValues();
        timeFrame.reset();
    }

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => dispatch(openModalTrackerAdvancedFilters({ open: !open }))}
            style={{ zIndex: 2, width: '320px', transform: 'translateX(320px)' }}
            contentStyle={{ overflowY: "hidden" }}
            classSidePanel={hasFilters() ? "work-order-advanced-filters-available" : ""}
        >
            <SidePanelBody
                style={{ overflowY: "auto" }}
            >
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['workOrder_tab_advancedFilters_title']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={clearFilters}
                        >
                            {messages['workOrder_tab_advancedFilters_clearAll']}
                        </span>
                    </Grid>
                </Grid>
                <div style={{ width: 270, marginTop: 10 }}>
                    <Form
                        ref={formRef.ref}
                        loading={loading}
                        onValidSubmit={() => applyChanges()}
                        onChangeValues={() => { }}
                        className="work-order-advanced-filters"
                    >
                        <Form.Item
                            name="scheduledAt"
                            label={messages['workOrder_tab_advancedFilters_scheduledDate']}
                        >
                            <div style={{ width: 270, marginTop: -10 }} className="work-order-advanced-filters-timeframe">
                                <TimeFrame
                                    isRequired
                                    filterSwitch={false}
                                    {...timeFrame}
                                    maxDate={moment().add(2, 'months').toDate()}
                                    dateFormat={dateformat || 'Y/m/d'}
                                    withTime={false}
                                    openDateRangePickerModalAutomatically={false}
                                    startDate={moment(formData?.rangeDate?.start)}
                                    endDate={moment(formData?.rangeDate?.end)}
                                />
                            </div>
                        </Form.Item>
                        <Form.Item
                            name="driver"
                            label={messages['workOrder_tab_advancedFilters_tags']}
                            style={{ marginBottom: "3px" }}
                        >
                            <div className="advance-filter-tag-picker">
                                <TagsManage
                                    onlyTags={true}
                                    enableSelectAll={false}
                                    disableUntaggedSelection={true}
                                    mode="selector"
                                    height={formData?.tags?.length > 0 ? 120 : 55}
                                    placeholder={messages["searchTags"] || "Search tags"}
                                    value={formData?.tags || []}
                                    onChange={(items) => {
                                        entityTags.onChange(items);
                                        setFormData({ ...formData, tags: items });
                                        savePreferences([{ key: "tags", value: items }])
                                    }}
                                    isVisiblePill={true}
                                    menuStyle={{ maxHeight: '250px', maxWidth: '250px', overflowY: 'auto' }}
                                />
                            </div>
                        </Form.Item>
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                name="driver"
                                textField="name"
                                label={messages['workOrder_tab_advancedFilters_driver']}
                                valueField="driverId"
                                placeholder={messages['workOrder_tab_advancedFilters_driver']}
                                isMulti={true}
                                options={drivers || []}
                                isDisabled={false}
                                isLoading={loadingDrivers || loading}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={formData?.drivers || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, drivers: value })
                                    savePreferences([{ key: "drivers", value: value }])
                                    formRef?.setValues({
                                        drivers: value || null
                                    })
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Select
                                name="customers"
                                textField="name"
                                label={messages['schedule_workOrder_customer']}
                                valueField="id"
                                placeholder={messages['schedule_workOrder_customer']}
                                isMulti={true}
                                options={customers || []}
                                isDisabled={false}
                                isLoading={loadingCustomers || loading}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={formData?.customers || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, customers: value })
                                    savePreferences([{ key: "customers", value: value }])
                                    formRef?.setValues({
                                        customers: value || null
                                    })
                                }} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                name="status"
                                textField="name"
                                label={messages['workOrder_tab_advancedFilters_status']}
                                valueField="id"
                                placeholder={messages['workOrder_tab_advancedFilters_status']}
                                isMulti={true}
                                options={statuses || []}
                                isDisabled={false}
                                isLoading={loading}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={formData?.status || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, status: value })
                                    savePreferences([{ key: "status", value: value }])
                                    formRef?.setValues({
                                        status: value || null
                                    })
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                name="type"
                                textField="name"
                                label={messages['workOrder_tab_advancedFilters_type']}
                                valueField="id"
                                placeholder={messages['workOrder_tab_advancedFilters_type']}
                                isMulti={true}
                                options={workOrderTypes || []}
                                isDisabled={false}
                                isLoading={loading}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={formData?.types || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, types: value })
                                    savePreferences([{ key: "types", value: value }])
                                    formRef?.setValues({
                                        types: value || null
                                    })
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                name="severity"
                                textField="name"
                                label={messages['workOrder_tab_advancedFilters_severity']}
                                valueField="id"
                                placeholder={messages['workOrder_tab_advancedFilters_severity']}
                                isMulti={true}
                                options={severityList || []}
                                isDisabled={false}
                                isLoading={loading}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={formData?.severities || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, severities: value })
                                    savePreferences([{ key: "severities", value: value }])
                                    formRef?.setValues({
                                        severities: value || null
                                    })
                                }}
                            />
                        </div>
                    </Form>
                </div>
            </SidePanelBody>
            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading || (!timeFrame?.startDate || !timeFrame?.endDate)}
                        onClick={() => formRef?.submit()}
                        variant="primary"
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default AdvancedFilters